import type { FC } from "react";

import { RadioButton, RadioButtonGroup } from "@jti/core-components";
import { Card } from "@jti/core-components/components/Card";
import { useSelector } from "@jti/store/reduxHooks";
import { DEPRECATED_Loading, useFormContext, useTranslations } from "@jti/ui";

import { SHIPPING_FORM_FIELDS } from "@checkout/forms/ShippingForm/constants";
import { useSubscription } from "@checkout/hooks";
import useDelivery from "@checkout/hooks/useDelivery";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";

import { DeliveryFormShippingOptionMethodLabel } from "../DeliveryFormShippingOptionMethodLabel";

import styles from "./DeliveryFormShippingOptions.module.scss";

type DeliveryFormShippingOptionsProps = {
  fieldName?: string;
};

export const DeliveryFormShippingOptions: FC<DeliveryFormShippingOptionsProps> = ({
  fieldName = SHIPPING_FORM_FIELDS.shippingMethodCode,
}) => {
  const { __ } = useTranslations();
  const formContext = useFormContext();
  const shippingMethodCode = formContext.watch(fieldName);
  const { isSubscription } = useSubscription();
  const { isFreeTrial } = useFreeTrial();
  const cartItems = useSelector((state) => state.cart.items);
  const isCartEmpty = cartItems.length === 0;

  const { filteredShippingMethods, shippingMethods } = useDelivery();

  const hasShippingOptions = shippingMethods.fetchStatus === "success" && filteredShippingMethods.length > 0;
  const isUnavailable = shippingMethods.fetchStatus === "error" || !hasShippingOptions || (!isFreeTrial && isCartEmpty);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formContext.setValue(fieldName, event.target.value);
  };

  const shippingOptions = filteredShippingMethods.map(
    ({ methodCode, disableForSubscription, available, ...props }) => ({
      value: methodCode,
      label: <DeliveryFormShippingOptionMethodLabel {...props} />,
      disabled: (isSubscription && disableForSubscription) || !available,
    })
  );

  return (
    <Card title={__("cart.checkout.shipping_options.title")} variant="level-1">
      {shippingMethods.fetchStatus === "pending" && <DEPRECATED_Loading />}

      {isUnavailable && <span>{__("cart.shipping_options.na")}</span>}

      {hasShippingOptions && !isUnavailable && (
        <div className={styles.options} data-testid="shipping-options">
          <RadioButtonGroup name={fieldName} orientation="vertical">
            {shippingOptions.map((opt) => (
              <RadioButton
                key={opt.value}
                value={opt.value}
                label={opt.label}
                disabled={opt.disabled}
                checked={shippingMethodCode === opt.value}
                onChange={handleChange}
              />
            ))}
          </RadioButtonGroup>
        </div>
      )}
    </Card>
  );
};
